import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import {
    CREATE_HOSPITAL_ADMIN_PATH
} from '@constants/routes';
import { IoClose } from 'react-icons/io5';
import ErrorMessage from '@components/Error/ErrorMessage';
import {
    addHospitalBlock,
    hospitalInfo,
    hospitalParentData,
} from '@utils/hospitalUtils';
import { scan } from '@utils/authUtils';
import { v4 as uuidv4 } from 'uuid';
import {
    HOSPITAL_ID
} from '@constants/dynamoDBAttributes';
import { MdInfo } from 'react-icons/md';
import Pagination from '@components/Layout/Pagination';
import HospitalDataTable from '@components/Layout/HospitalDataTable';
import DataLoader from '@components/DataLoader';
import Swal from 'sweetalert2';
import {
    formatDateDMY,
    generateUniqueTimestamp,
    getHospitalAdminList,
} from '@utils/sharedUtils';
import SearchBar from '@components/Layout/SearchBar';
import { DEFAULT_PROFILE_SRC } from '@constants/images';
import { DATA_TEXT_MAX_LENGTH, DATA_TEXT_MIN_LENGTH } from '@constants/form';
import AdminListTable from '@components/Layout/AdminListTable';
import {
    setUserRole,
    setDeviceUniqueID,
    setHospitalName,
    setHospitalBranch,
    setHospitalID,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setHospitalWebsite,
    setHospitalEmail,
    setHospitalContact,
    setHospitalGST,
    setFileMetadata,
    setName,
    setEmail,
    setMobileNumber,
    setPassword,
    setAdminCreateHospital,
    setHospitalUser,
} from '@store/reducers/auth/signupSlice';
import { useDispatch } from 'react-redux';
import { HOSPITAL } from '@constants/constants';
import { useAuditLog } from '../../../contexts/AuditLogContext';
import HierarchyModalPopUp from '@components/TreeView/HierarchyModalPopUp';
interface FormData {
    parent: string;
    name?: string;
}

const MySwal = Swal;

const HospitalDashboard = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState<FormData>({ parent: '' });
    const [selectType, setSelectType] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [parents, setParents] = useState<any[]>([]);
    const [hospitalUserDetail, setHospitalUserDetail] = useState<any | null>(
        null
    );
    const [hospitalAdminList, setHospitalAdminList] = useState<any[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const hospitalLogoUrl = useAppSelector(
        (state) => state.login.hospitalLogoUrl
    );
    const loggedInHospitalId = useAppSelector(
        (state) => state.login.loggedInHospitalId
    );
    const loggedInUserEmail = useAppSelector(
        (state) => state.login.loggedInUserEmail
    );
    const loggedInUserMobilenumber = useAppSelector(
        (state) => state.login.loggedInUserMobilenumber
    );
    const loggedInHospitalGstNo = useAppSelector(
        (state) => state.login.loggedInHospitalGstNo
    );
    const loggedInHospitalWebSite = useAppSelector(
        (state) => state.login.loggedInHospitalWebSite
    );
    const dispatch = useDispatch();
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const { addLog } = useAuditLog();

    const fetchData = async () => {
        try {
            const data = await hospitalParentData(loggedInHospitalId);
            if (data && data.length != 0) {
                const uuidNameMap = data.reduce<{ [key: string]: string }>(
                    (acc, item) => {
                        if (item.uuid?.S && item.name?.S) {
                            acc[item.uuid.S] = item.name.S;
                        }
                        return acc;
                    },
                    {}
                );

                const processedData = data.map((item) => {
                    const parentUuid = item.parent_id?.S;
                    const parentName = parentUuid
                        ? uuidNameMap[parentUuid]
                        : '';

                    return {
                        id: item.uuid.S,
                        name: item.name.S,
                        parent: parentName,
                        parent_id: item.parent_id?.S,
                        created_at: item.created_at?.S
                            ? formatDateDMY(item.created_at.S)
                            : '',
                        createdAt_time: item.created_at?.S
                            ? item.created_at.S
                            : '',
                    };
                });
                setParents(processedData);
            }
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setLoading(false);
        }
    };

    const hospitalDetails = async () => {
        const hosData = await hospitalInfo(loggedInUserTenantID);
        if (hosData) {
            setHospitalUserDetail(hosData);
            fetchData();
        }
    };

    const adminList = async () => {
        const hosAdminData = await getHospitalAdminList(loggedInUserTenantID);
        if (hosAdminData) {
            setHospitalAdminList(hosAdminData);
        }
    };

    useEffect(() => {
        hospitalDetails();
        adminList();
    }, []);

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('hospital_info_list.name'),
        },
        {
            id: 2,
            title: t('hospital_info_list.parent'),
        },
        {
            id: 3,
            title: t('hospital_info_list.created_at'),
        },
        {
            id: 4,
            title: t('hospital_info_list.action'),
        },
    ]);

    const [tableHeading1, setTableHeading1] = useState([
        {
            id: 1,
            title: t('management.hospital.admin_title'),
        },
        {
            id: 2,
            title: t('management.hospital.email_title'),
        },
        {
            id: 3,
            title: t('management.hospital.mobile_num'),
        },
        {
            id: 4,
            title: t('management.hospital.action'),
        },
    ]);

    const handleParentChange = async (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
        setError(null);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
        setError(null);
    };

    const handleAddData = () => {
        setSelectType('');
        setFormData({ parent: '', name: '' });
        setShowModal(true);
    };

    const handleHierarchy = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectType('');
        setFormData({ parent: '' });
        setShowModal(false);
        setError(null);
    };

    const filteredUsers = useMemo(() => {
        let filtered = parents;
        if (searchTerm) {
            filtered = filtered.filter(
                (user) =>
                    user.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    user.parent
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, parents]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt_time);
                const dateB = new Date(b.createdAt_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() === 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() === 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() === 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            const hospitalDetails = await scan(
                'hospitals',
                loggedInUserTenantID
            );

            if (hospitalDetails) {
                const details = hospitalDetails[0];
                const hospital_uuid = details?.[HOSPITAL_ID]?.S ?? '';

                const newData = {
                    ...formData,
                    id: uuidv4(),
                    date: generateUniqueTimestamp(),
                };

                await addHospitalBlock(
                    newData,
                    loggedInUserTenantID,
                    hospital_uuid
                );
                addLog(
                    'Hospital Data Created',
                    newData.id,
                    'Hospital Block',
                    loggedInUserID,
                    { 'Hospital Block': newData.name, 'Parent': newData.parent ? newData.parent : '' },
                    window.location.hostname
                );
                MySwal.fire({
                    position: 'top-end', // Position the toast in the top-right corner
                    icon: 'success',
                    title: 'Data saved successfully',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    customClass: {
                        popup: 'text-green bg-light_green', // Add custom class to the popup
                    },
                });
                setShowModal(false);
                setError(null);
                setFormData({ parent: '' });
                setSelectType('');
            }
        } catch (error) {
            console.error('Error saving data to DynamoDB:', error);
            setError('Error saving data. Please try again.');
        } finally {
            fetchData();
        }
    };

    function handelHospitalAdmin() {
        dispatch(setUserRole(HOSPITAL));
        dispatch(setAdminCreateHospital(true));
        dispatch(setDeviceUniqueID(''));
        dispatch(setHospitalName(''));
        dispatch(setHospitalBranch(''));
        dispatch(setHospitalID(''));
        dispatch(setAddressOne(''));
        dispatch(setAddressTwo(''));
        dispatch(setCity(''));
        dispatch(setUserState(''));
        dispatch(setCountry(''));
        dispatch(setPincode(''));
        dispatch(setHospitalWebsite(''));
        dispatch(setHospitalEmail(''));
        dispatch(setHospitalContact(''));
        dispatch(setHospitalGST(''));
        dispatch(
            setFileMetadata({
                name: '',
                url: '',
                type: '',
            })
        );
        dispatch(setName(''));
        dispatch(setEmail(''));
        dispatch(setMobileNumber(''));
        dispatch(setPassword(''));
        dispatch(setHospitalUser(true));
        navigate(CREATE_HOSPITAL_ADMIN_PATH);
    }

    return (
        <>
            <div className="mx-auto px-5">
                <div className="d-flex justify-content-between align-items-center">
                    <h1 className="text-lg font-bold">
                        {t('management.hospital.title')}
                    </h1>

                    {showModal && (
                        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-800 bg-opacity-50 z-50">
                            <div className="bg-white rounded-lg p-6 w-96 relative">
                                <h2 className="text-lg font-semibold mb-3 text-center text-black">
                                    {t('hospital.add_data')}
                                </h2>
                                <span
                                    className="absolute right-0 top-5 cursor-pointer"
                                    onClick={handleCloseModal}
                                >
                                    <span className="absolute text-xs top-2 right-10 cursor-pointer text-black">
                                        {t('hospital.close')}
                                    </span>
                                    <span className="absolute top-2 right-5 cursor-pointer text-black">
                                        <IoClose />
                                    </span>
                                </span>
                                <div className="bg-white w-64 mx-auto">
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-4">
                                            <label
                                                htmlFor="name"
                                                className="block mb-1 text-sm font-normal"
                                            >
                                                {t('hospital.name')}
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                className="w-full border rounded p-2 placeholder-gains_boro"
                                                placeholder="Enter The Name"
                                                value={formData.name || ''}
                                                onChange={handleInputChange}
                                                minLength={DATA_TEXT_MIN_LENGTH}
                                                maxLength={DATA_TEXT_MAX_LENGTH}
                                                required
                                            />
                                            <div className="relative row mt-2">
                                                <MdInfo className="text-secondary col-2 px-0 text-base text-center mt-3" />
                                                <span className="text-sm font-normal col-10 px-0">
                                                    {t('hospital.discription')}
                                                </span>
                                            </div>
                                        </div>
                                        {parents && parents.length != 0 && (
                                            <div className="mb-4">
                                                <label
                                                    htmlFor="parent"
                                                    className="block mb-1"
                                                >
                                                    {t(
                                                        'hospital.select_parent'
                                                    )}
                                                </label>
                                                <select
                                                    id="parent"
                                                    name="parent"
                                                    className="w-full border rounded p-2"
                                                    value={
                                                        formData.parent || ''
                                                    }
                                                    onChange={
                                                        handleParentChange
                                                    }
                                                >
                                                    <option value="">
                                                        {t('hospital.select')}
                                                    </option>
                                                    {parents.map((parent) => (
                                                        <option
                                                            key={parent.id}
                                                            value={parent.id}
                                                        >
                                                            {parent.parent_id
                                                                ? `${parent.name} (${parent.parent})`
                                                                : parent.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="relative row mt-2">
                                                    <MdInfo className="text-secondary col-2 px-0 text-lg text-center mt-3" />
                                                    <span className="text-sm font-normal col-10 px-0">
                                                        {t(
                                                            'hospital.parent_discription'
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {error && (
                                            <ErrorMessage message={error} />
                                        )}
                                        <button
                                            className="bg-primary text-white py-2 px-4 rounded cursor-pointer w-full"
                                            type="submit"
                                        >
                                            {t('hospital.submit')}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="card relative rounded-2xl mt-5 w-full mx-auto">
                    <div className="hospital-logo">
                        {hospitalLogoUrl ? (
                            <img
                                src={hospitalLogoUrl}
                                alt="Logo"
                                className="object-contain rounded-full mx-auto"
                            />
                        ) : (
                            <img
                                src={DEFAULT_PROFILE_SRC}
                                alt="Logo"
                                className="object-contain rounded-full mx-auto"
                            />
                        )}
                    </div>
                    <div className="flex flex-wrap mb-4 pt-24">
                        <div className="flex w-1/2 pl-8 ">
                            <div className="w-1/3 flex flex-col">
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black">
                                        {t('nurse_details.hospital_name')}
                                    </label>
                                </div>
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black">
                                        {t('nurse_details.hospital_id')}
                                    </label>
                                </div>
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black">
                                        {t(
                                            'management.hospital.hospital_website'
                                        )}
                                    </label>
                                </div>
                            </div>
                            <div className="w-2/3 flex flex-col">
                                <div className="py-2 flex items-center">
                                    <span className="ml-3">
                                        {hospitalUserDetail?.hospital_name}
                                    </span>
                                </div>
                                <div className="py-2 flex items-center">
                                    <span className="ml-3">
                                        {hospitalUserDetail?.hospital_id.toUpperCase()}
                                    </span>
                                </div>
                                <div className="py-2 flex items-center">
                                    <span className="ml-3">
                                        {loggedInHospitalWebSite
                                            ? loggedInHospitalWebSite
                                            : '--'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex w-1/2 pr-12">
                            <div className="w-1/3 flex flex-col">
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black text-wrap">
                                        {t('management.hospital.gst_no')}
                                    </label>
                                </div>
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black text-wrap">
                                        {t('management.hospital.mobile_no')}
                                    </label>
                                </div>
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black text-wrap">
                                        {t('management.hospital.email_id')}
                                    </label>
                                </div>
                                <div className="py-2 flex items-center">
                                    <label className="font-bold text-black text-wrap">
                                        {t('nurse_details.address')}
                                    </label>
                                </div>
                            </div>
                            <div className="w-2/3 flex flex-col">
                                <div className="py-2 flex items-center">
                                    <span className="ml-3 text-wrap">
                                        {loggedInHospitalGstNo &&
                                        loggedInHospitalGstNo.trim() !== ''
                                            ? loggedInHospitalGstNo
                                            : '--'}
                                    </span>
                                </div>
                                <div className="py-2 flex items-center">
                                    <span className="ml-3 text-wrap">
                                        {loggedInUserMobilenumber}
                                    </span>
                                </div>
                                <div className="py-2 flex items-center">
                                    <span className="ml-3 text-wrap">
                                        {loggedInUserEmail}
                                    </span>
                                </div>
                                <div className="py-2 flex items-center">
                                    <span className="ml-3 text-wrap">
                                        {hospitalUserDetail?.address}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-between items-center mb-4 mt-5">
                    <h1 className="text-lg font-bold">
                        {t('management.hospital.admin_list')}
                    </h1>
                    <button
                        className="bg-primary rounded ml-4 py-1.5 px-3 text-white w-32"
                        onClick={handelHospitalAdmin}
                    >
                        {t('management.hospital.add_admin')}
                    </button>
                </div>
                <AdminListTable
                    data={hospitalAdminList}
                    header={tableHeading1}
                />
                <div className="flex justify-between items-center mb-4 mt-5">
                    <h1 className="text-lg font-bold">
                        {t('management.hospital.title1')}
                    </h1>
                    <div>
                        <button
                            className="bg-primary rounded ml-4 py-1.5 px-3 text-white"
                            onClick={handleHierarchy}
                        >
                            {t('management.hospital.view_hierarchy')}
                        </button>
                        <button
                            className="bg-primary rounded ml-4 py-1.5 px-3 text-white w-32"
                            onClick={handleAddData}
                        >
                            {t('management.hospital.hospital_data')}
                        </button>
                    </div>
                </div>

                <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />
                <HospitalDataTable
                    data={paginatedUsers}
                    header={tableHeading}
                    parents={parents}
                    fetchData={fetchData}
                />
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
            <HierarchyModalPopUp
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
            {loading && <DataLoader />}
        </>
    );
};
export default HospitalDashboard;
